import { ROUTES } from "#shared/routes";
import { MaterialVolumetricPricingStrategySchema, PricingModeSchema } from "shared";

export type OnboardingMaterialMode =
  | "RAW"
  | "VOLUMETRIC_REAL_WEIGHT"
  | "VOLUMETRIC_CAPABLE_RECTANGLE";

export const getOnboardingMaterialMode = (companySettings: {
  materialPricingMode: string;
  materialVolumetricPricingStrategy: string;
}): OnboardingMaterialMode => {
  if (companySettings.materialPricingMode === PricingModeSchema.enum.RAW) {
    return "RAW";
  }

  return companySettings.materialVolumetricPricingStrategy ===
    MaterialVolumetricPricingStrategySchema.enum.REAL_WEIGHT
    ? "VOLUMETRIC_REAL_WEIGHT"
    : "VOLUMETRIC_CAPABLE_RECTANGLE";
};

export type OnboardingStepId =
  | "activityType"
  | "materialMode"
  | "materialSpecification"
  | "rawMaterialSpecification"
  | "processStepConfiguration"
  | "defaultProcessSteps";

export type OnboardingStepContext = {
  materialMode: OnboardingMaterialMode | null;
  hasProcessSteps: boolean;
};

type OnboardingStepConfig = {
  path: string;
  position: number;
  next: (ctx: OnboardingStepContext) => string;
  prev: (ctx: OnboardingStepContext) => string;
};

export const ONBOARDING_FINISH_PATH = ROUTES.v2OnboardingFinish;

const materialSpecificationStepId = (ctx: OnboardingStepContext): OnboardingStepId =>
  ctx.materialMode === "RAW" ? "rawMaterialSpecification" : "materialSpecification";

export const ONBOARDING_STEPS: Record<OnboardingStepId, OnboardingStepConfig> = {
  activityType: {
    path: ROUTES.v2OnboardingActivityType,
    position: 1,
    next: () => ONBOARDING_STEPS.materialMode.path,
    prev: () => ROUTES.v2Onboarding,
  },
  materialMode: {
    path: ROUTES.v2OnboardingMaterialMode,
    position: 2,
    next: (ctx) => ONBOARDING_STEPS[materialSpecificationStepId(ctx)].path,
    prev: () => ONBOARDING_STEPS.activityType.path,
  },
  materialSpecification: {
    path: ROUTES.v2OnboardingMaterialSpecification,
    position: 3,
    next: () => ONBOARDING_STEPS.processStepConfiguration.path,
    prev: () => ONBOARDING_STEPS.materialMode.path,
  },
  rawMaterialSpecification: {
    path: ROUTES.v2OnboardingRawMaterialSpecification,
    position: 3,
    next: () => ONBOARDING_STEPS.processStepConfiguration.path,
    prev: () => ONBOARDING_STEPS.materialMode.path,
  },
  processStepConfiguration: {
    path: ROUTES.v2OnboardingProcessStepConfiguration,
    position: 4,
    next: (ctx) =>
      ctx.hasProcessSteps ? ONBOARDING_STEPS.defaultProcessSteps.path : ONBOARDING_FINISH_PATH,
    prev: (ctx) => ONBOARDING_STEPS[materialSpecificationStepId(ctx)].path,
  },
  defaultProcessSteps: {
    path: ROUTES.v2OnboardingDefaultProcessSteps,
    position: 5,
    next: () => ONBOARDING_FINISH_PATH,
    prev: () => ONBOARDING_STEPS.processStepConfiguration.path,
  },
};

export const ONBOARDING_STEP_COUNT = new Set(
  Object.values(ONBOARDING_STEPS).map(({ position }) => position),
).size;
